import { memo, Fragment } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { isMacOS } from "@/utils/utils";
import {
  KEYBOARD_SHORTCUTS_CONFIG,
  KEYBOARD_SHORTCUT_DISPLAY_HELPERS,
} from "@/hooks/keyboard-shortcuts/keyboardShortcutsConfig";

const { parseKeyString, formatKey } = KEYBOARD_SHORTCUT_DISPLAY_HELPERS;

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const getTabValue = (title: string): string => {
  return title
    .toLowerCase()
    .replace(/[&\s]+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
};

const KeyboardShortcutsModal = memo(({ isOpen, onClose }: Props) => {
  const isMac = isMacOS();
  const defaultTab = KEYBOARD_SHORTCUTS_CONFIG[0]
    ? getTabValue(KEYBOARD_SHORTCUTS_CONFIG[0].title)
    : "";

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader className="pb-2">
          <DialogTitle className="text-lg font-semibold">
            Keyboard Shortcuts
          </DialogTitle>
        </DialogHeader>

        <Tabs
          defaultValue={defaultTab}
          variant="underline"
          className="flex-1 flex flex-col overflow-hidden"
        >
          <TabsList className="w-full justify-start border-b rounded-none bg-transparent p-0 h-auto">
            {KEYBOARD_SHORTCUTS_CONFIG.map((category) => {
              const tabValue = getTabValue(category.title);
              return (
                <TabsTrigger
                  key={category.title}
                  value={tabValue}
                  className="data-[state=active]:bg-transparent"
                >
                  {category.title}
                </TabsTrigger>
              );
            })}
          </TabsList>

          <ScrollArea
            className="flex-1 mt-4"
            viewportClassName="max-h-[520px] pr-2"
          >
            {KEYBOARD_SHORTCUTS_CONFIG.map((category) => {
              const tabValue = getTabValue(category.title);
              return (
                <TabsContent
                  key={category.title}
                  value={tabValue}
                  className="mt-0 space-y-2"
                >
                  {category.shortcuts.map((shortcut) => {
                    const keys = parseKeyString(shortcut.key);

                    return (
                      <div
                        key={shortcut.key}
                        className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-muted/50 transition-colors"
                      >
                        <span className="text-sm text-foreground">
                          {shortcut.description}
                        </span>
                        <div className="flex items-center gap-1">
                          {keys.map((key, index) => (
                            <Fragment key={index}>
                              <kbd className="inline-flex min-w-[24px] h-6 items-center justify-center rounded border border-border/50 bg-muted px-2 text-xs font-medium text-foreground shadow-xs">
                                {formatKey(key, isMac)}
                              </kbd>
                              {index < keys.length - 1 && !isMac && (
                                <span className="text-xs text-muted-foreground select-none">
                                  +
                                </span>
                              )}
                            </Fragment>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </TabsContent>
              );
            })}
          </ScrollArea>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
});

KeyboardShortcutsModal.displayName = "KeyboardShortcutsModal";

export default KeyboardShortcutsModal;
